import { Link, useHref, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type { QuoteAccepted } from "@shared-core/data/customer-link-adapter";
import { QuotePageBody } from "../customer-pages/QuotePageBody";
import type { QuotePageActions } from "../customer-pages/QuotePageBody";
import { useGoTo, useLinkRead } from "../customer-pages/useLinkRead";
import {
  CisRecordError,
  CisRecordLoading,
  CisRecordNotFound,
} from "./CisRecordStates";
import { usePortalPageSlots } from "./PortalPageSlots";
import { usePortalViewer } from "./PortalViewerContext";
import { ArrowLeft } from "lucide-react";

// A CIS quote's own page for the signed-in customer (customer-storefront P4g, spec 26 section 9B): the emailed
// link's quote page body, unchanged, read by quote number with the session (`GET /views/customer/quote-page/
// {quote_no}`) inside the portal's layout, from the Quotes list.
//
// Accepting, declining, the artwork and the PDF go to the URLs the read named (`accept.url`, `decline_url`, the
// artwork URLs, `pdf_url`), as the signed-in person. The payment methods Accept offers are the read's: a method
// the server leaves out is not offered, and the page does not second-guess the list. The staff view-as shell
// reads the same page from its mirror, which names no URL that acts, and hands the body no actions.

const CANNOT_HERE = "This can't be done from here. Please reload the page.";

export function PortalQuoteDetailPage() {
  const { quoteNo = "" } = useParams<{ quoteNo: string }>();
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl, isStaffView } = usePortalViewer();
  const goTo = useGoTo();
  const slots = usePortalPageSlots();

  const { state, reload } = useLinkRead(
    `${viewKey}|${quoteNo}`,
    async () => {
      const page = await adapter.getQuotePage(quoteNo);
      return page
        ? { kind: "ok" as const, view: page }
        : { kind: "not_found" as const };
    },
    "We couldn't load this quote.",
  );

  const quotesTo = buildPortalUrl(paths.portalQuotes());
  // The quote that replaced this one opens in the portal like any other: the body links to it as a plain
  // address, so it is resolved here against the router (a staff view lives under its own base path).
  const replacedBy =
    state.kind === "ok" ? (state.view.replaced_by_quote_no ?? "") : "";
  const replacementHref = useHref(
    buildPortalUrl(paths.portalQuoteDetail({ quoteNo: replacedBy })),
  );

  if (state.kind === "loading") return <CisRecordLoading what="quote" />;
  if (state.kind === "error")
    return (
      <CisRecordError
        what="quote"
        message={state.message}
        onRetry={() => void reload()}
      />
    );
  if (state.kind === "not_found")
    return <CisRecordNotFound what="quote" backTo={quotesTo} />;

  const page = state.view;

  // Card: Stripe's checkout, which returns to the order's page. Otherwise straight to the order the quote
  // became, with its thank-you.
  function onAccepted(accepted: QuoteAccepted) {
    if (accepted.checkout_url) goTo(accepted.checkout_url);
    else
      goTo(`${paths.portalCisOrder({ orderNo: accepted.order_no })}?placed=1`);
  }

  const actions: QuotePageActions | null = isStaffView
    ? null
    : {
        accept: (input) => {
          if (page.accept === null || page.accept === undefined)
            return Promise.reject(new Error(CANNOT_HERE));
          return adapter.acceptQuote(page.accept.url, input);
        },
        onAccepted,
        decline: (reason) => {
          if (page.decline_url === null)
            return Promise.reject(new Error(CANNOT_HERE));
          return adapter.declineQuote(page.decline_url, reason);
        },
        reload,
        writes: adapter.artwork,
        fetchFile: adapter.fetchFile,
        pdfUrl: page.pdf_url,
      };

  return (
    <QuotePageBody
      backLink={
        <Link
          to={quotesTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Quotes
        </Link>
      }
      page={page}
      actions={actions}
      replacementHref={() => replacementHref}
      slot={slots.quotePage?.(page, reload)}
    />
  );
}
